import { addDays, isDateOnly, parts, type BusinessCalendar, type DateOnly } from '@/domain/dates'
import { workingDaysBetween } from './calendar'
import type { Balance, Department, Employee, LeaveRequest, LeaveType, Role } from './types'

// ---------------------------------------------------------------------------
// Permissions (single source, mirrored by RLS in the database)
// ---------------------------------------------------------------------------

export type Permission =
  | 'requests.approve' // decide requests of direct reports (manager) or anyone (admin)
  | 'requests.book_for_others' // HR books on behalf of someone
  | 'team.view' // balances, calendar and reports of the people they supervise
  | 'people.manage' // employees, departments
  | 'settings.manage' // holidays, bridges, rules

const MATRIX: Record<Role, Permission[]> = {
  employee: [],
  manager: ['requests.approve', 'team.view'],
  hr: ['requests.book_for_others', 'team.view', 'people.manage', 'settings.manage'],
  admin: ['requests.approve', 'requests.book_for_others', 'team.view', 'people.manage', 'settings.manage'],
}

export function can(role: Role | undefined, permission: Permission): boolean {
  return role ? MATRIX[role].includes(permission) : false
}

export const ROLE_META: Record<Role, { label: string; description: string }> = {
  employee: { label: 'Colaborador', description: 'Marca e acompanha as próprias férias.' },
  manager: { label: 'Gestor', description: 'Aprova os pedidos da sua equipa direta.' },
  hr: { label: 'Recursos Humanos', description: 'Gere pessoas, saldos e calendário. Não aprova pedidos.' },
  admin: { label: 'Administrador', description: 'Acesso total à organização.' },
}

/** People a user supervises: direct reports for managers, everyone for HR/admin. */
export function supervisedIds(role: Role, me: string | null, employees: Employee[]): Set<string> {
  if (role === 'hr' || role === 'admin') return new Set(employees.map((e) => e.id))
  if (role === 'manager' && me) return new Set(employees.filter((e) => e.managerId === me).map((e) => e.id))
  return new Set()
}

// ---------------------------------------------------------------------------
// Request validation (UI preview and API use the same rules)
// ---------------------------------------------------------------------------

export interface ValidationIssue {
  level: 'error' | 'warning'
  code: string
  message: string
}

export interface TeamOverlap {
  day: DateOnly
  people: string[]
}

/** People of the same department away (approved or pending) on each working day of the period. */
export function teamOverlaps(args: {
  calendar: BusinessCalendar
  employee: Employee
  employees: Employee[]
  requests: LeaveRequest[]
  start: DateOnly
  end: DateOnly
  ignoreRequestId?: string
}): TeamOverlap[] {
  const { calendar, employee, employees, requests, start, end, ignoreRequestId } = args
  if (!employee.departmentId) return []
  const colleagues = new Map(employees.filter((e) => e.departmentId === employee.departmentId && e.id !== employee.id && e.active).map((e) => [e.id, e]))
  const out: TeamOverlap[] = []
  for (let d = start; d <= end; d = addDays(d, 1)) {
    if (!calendar.isBusinessDay(d)) continue
    const people = requests
      .filter((r) => r.id !== ignoreRequestId && (r.status === 'approved' || r.status === 'pending') && colleagues.has(r.employeeId) && r.start <= d && r.end >= d)
      .map((r) => colleagues.get(r.employeeId)!.name)
    if (people.length) out.push({ day: d, people: [...new Set(people)] })
  }
  return out
}

export function validateRequest(args: {
  calendar: BusinessCalendar
  today: DateOnly
  type: LeaveType
  start: DateOnly
  end: DateOnly
  employee: Employee
  employees: Employee[]
  department: Department | undefined
  requests: LeaveRequest[]
  balance: Balance
  ignoreRequestId?: string
}): { workingDays: number; issues: ValidationIssue[]; overlaps: TeamOverlap[] } {
  const { calendar, today, type, start, end, employee, requests, balance, department, ignoreRequestId } = args
  const issues: ValidationIssue[] = []
  if (!isDateOnly(start) || !isDateOnly(end)) {
    return { workingDays: 0, issues: [{ level: 'error', code: 'invalid_dates', message: 'Indique datas válidas.' }], overlaps: [] }
  }
  if (start < today) issues.push({ level: 'error', code: 'past', message: 'Não é possível marcar férias em datas passadas.' })
  if (end < start) issues.push({ level: 'error', code: 'order', message: 'A data de fim tem de ser igual ou posterior à de início.' })
  if (parts(start).year > parts(today).year + 2) {
    issues.push({ level: 'error', code: 'far', message: 'Só é possível marcar até 2 anos civis à frente.' })
  }
  const workingDays = end >= start ? workingDaysBetween(calendar, start, end) : 0
  if (end >= start && workingDays === 0) issues.push({ level: 'error', code: 'no_days', message: 'O período escolhido não tem dias úteis.' })
  if (type === 'birthday' && workingDays !== 1) {
    issues.push({ level: 'error', code: 'birthday_one_day', message: 'O dia de aniversário é exatamente 1 dia útil.' })
  }

  const mine = requests.filter((r) => r.employeeId === employee.id && r.id !== ignoreRequestId && (r.status === 'approved' || r.status === 'pending'))
  if (mine.some((r) => r.start <= end && r.end >= start)) {
    issues.push({ level: 'error', code: 'overlap', message: 'Já existe um pedido aprovado ou pendente nestas datas.' })
  }
  if (type === 'birthday' && mine.some((r) => r.type === 'birthday' && r.start.slice(0, 4) === start.slice(0, 4))) {
    issues.push({ level: 'error', code: 'birthday_twice', message: 'Já existe um dia de aniversário marcado para este ano.' })
  }
  if (type === 'vacation' && workingDays > balance.available) {
    issues.push({
      level: 'error',
      code: 'balance',
      message: `Saldo insuficiente: tem ${balance.available} ${balance.available === 1 ? 'dia disponível' : 'dias disponíveis'} e este período usa ${workingDays}.`,
    })
  }

  const overlaps = end >= start ? teamOverlaps({ ...args, ignoreRequestId }) : []
  const max = department?.maxConcurrent ?? null
  if (type === 'vacation' && max !== null) {
    const full = overlaps.filter((o) => o.people.length >= max)
    if (full.length) {
      issues.push({
        level: 'error',
        code: 'team_limit',
        message: `Limite da equipa atingido: já há ${max} ${max === 1 ? 'pessoa' : 'pessoas'} de ${department!.name} fora em ${full.length} ${full.length === 1 ? 'dia' : 'dias'} deste período.`,
      })
    }
  }
  if (type === 'birthday' && employee.birthDate && isDateOnly(start)) {
    const bday = `${start.slice(0, 4)}${employee.birthDate.slice(4)}` as DateOnly
    const windowStart = addDays(bday, -15)
    const windowEnd = addDays(bday, 15)
    if (start < windowStart || start > windowEnd) {
      issues.push({ level: 'warning', code: 'birthday_window', message: 'Este dia fica longe do aniversário (±15 dias). O gestor vai ser avisado.' })
    }
  }
  return { workingDays, issues, overlaps }
}
